import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';
import { PREVENTIVE_TASK_ASSIGNEE_ENDPOINT } from '../../config/endpoints';

import {
  DEFAULT_GET_CONFIG,
  DEFAULT_PATCH_CONFIG,
  DEFAULT_POST_CONFIG,
  DEFAULT_PUT_CONFIG,
} from '../../config/rest';
import { isErrorResponse } from '../../utils/error-handlers';
import { PREVENTIVE_TASK_ASSIGNEE_PATH } from '../../config/paths';
import {
  CLEAR_PREVENTIVE_TASK_ASSIGNEE,
  CLEAR_PREVENTIVE_TASK_ASSIGNEES,
  FLAG_GETTING_PREVENTIVE_TASK_ASSIGNEES,
  FLAG_PREVENTIVE_TASK_ASSIGNEE_ACTIVITY,
  GET_PREVENTIVE_TASK_ASSIGNEE,
  GET_PREVENTIVE_TASK_ASSIGNEES,
} from '../types/task-schedule';

const flagGettingPreventiveTaskAssignees = (payload) => ({
  type: FLAG_GETTING_PREVENTIVE_TASK_ASSIGNEES,
  payload,
});

const flagPreventiveTaskAssigneeActivity = (payload) => ({
  type: FLAG_PREVENTIVE_TASK_ASSIGNEE_ACTIVITY,
  payload,
});

const getPreventiveTaskAssignees = (tableFilters) => async (dispatch) => {
  try {
    dispatch(flagGettingPreventiveTaskAssignees(true));

    const query = tableFilters;

    const url = `${PREVENTIVE_TASK_ASSIGNEE_ENDPOINT}?${QueryString.stringify(
      query,
    )}`;

    const response = await fetch(url, {
      ...DEFAULT_GET_CONFIG,
    });

    await isErrorResponse(response, null, dispatch);

    const preventiveTaskAssignees = await response.json();

    dispatch({
      type: GET_PREVENTIVE_TASK_ASSIGNEES,
      payload: preventiveTaskAssignees,
    });
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagGettingPreventiveTaskAssignees(false));
  }
};

const getPreventiveTaskAssigneesByPreventiveTaskId =
  ({ preventiveTaskId }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingPreventiveTaskAssignees(true));

      const url = `${PREVENTIVE_TASK_ASSIGNEE_ENDPOINT}/${preventiveTaskId}/assignee`;

      const response = await fetch(url, {
        ...DEFAULT_GET_CONFIG,
      });

      await isErrorResponse(response, null, dispatch);

      const preventiveTaskAssignees = await response.json();

      dispatch({
        type: GET_PREVENTIVE_TASK_ASSIGNEES,
        payload: preventiveTaskAssignees,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagGettingPreventiveTaskAssignees(false));
    }
  };

const getPreventiveTaskAssignee =
  ({ preventiveTaskAssigneeId }) =>
  async (dispatch) => {
    try {
      dispatch(flagPreventiveTaskAssigneeActivity(true));

      const url = `${PREVENTIVE_TASK_ASSIGNEE_ENDPOINT}/${preventiveTaskAssigneeId}`;

      const response = await fetch(url, {
        ...DEFAULT_GET_CONFIG,
      });

      await isErrorResponse(response, null, dispatch);

      const preventiveTaskAssignee = await response.json();

      dispatch({
        type: GET_PREVENTIVE_TASK_ASSIGNEE,
        payload: preventiveTaskAssignee,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagPreventiveTaskAssigneeActivity(false));
    }
  };

const postPreventiveTaskAssignee =
  (preventiveTaskAssigneeRequest) => async (dispatch) => {
    try {
      dispatch(flagPreventiveTaskAssigneeActivity(true));

      const response = await fetch(PREVENTIVE_TASK_ASSIGNEE_ENDPOINT, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(preventiveTaskAssigneeRequest),
      });

      await isErrorResponse(response, null, dispatch);

      const savedPreventiveTaskAssignees = await response.json();

      if (
        !Array.isArray(savedPreventiveTaskAssignees) ||
        savedPreventiveTaskAssignees.length === 0
      ) {
        throw new Error('No se registraron asignaciones.');
      }

      const total = savedPreventiveTaskAssignees.length;

      toastr.success(
        'Éxito',
        total === 1
          ? 'Asignación registrada correctamente'
          : `${total} asignaciones registradas correctamente`,
      );

      dispatch(push(PREVENTIVE_TASK_ASSIGNEE_PATH));

      return savedPreventiveTaskAssignees;
    } catch (error) {
      toastr.error(
        'Error',
        error.message || 'No se pudo registrar la asignación.',
      );

      return null;
    } finally {
      dispatch(flagPreventiveTaskAssigneeActivity(false));
    }
  };

const putPreventiveTaskAssignee =
  (preventiveTaskAssigneeId, preventiveTaskAssignee) => async (dispatch) => {
    try {
      dispatch(flagPreventiveTaskAssigneeActivity(true));

      const payload = {
        ...preventiveTaskAssignee,
        id: preventiveTaskAssigneeId,
      };

      const url = `${PREVENTIVE_TASK_ASSIGNEE_ENDPOINT}/${preventiveTaskAssigneeId}`;

      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(response, null, dispatch);

      const savedPreventiveTaskAssignee = await response.json();

      toastr.success('Éxito', 'Asignación actualizada correctamente');

      dispatch(
        push(
          `${PREVENTIVE_TASK_ASSIGNEE_PATH}/${savedPreventiveTaskAssignee.id}`,
        ),
      );
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagPreventiveTaskAssigneeActivity(false));
    }
  };

const patchDeactivatePreventiveTaskAssignee =
  (preventiveTaskAssigneeId) => async (dispatch) => {
    try {
      dispatch(flagPreventiveTaskAssigneeActivity(true));

      const url = `${PREVENTIVE_TASK_ASSIGNEE_ENDPOINT}/${preventiveTaskAssigneeId}`;

      const response = await fetch(url, {
        ...DEFAULT_PATCH_CONFIG,
      });

      await isErrorResponse(response, null, dispatch);

      await response.json();

      toastr.success('Éxito', 'Asignación desactivada correctamente');

      const getUrl = `${PREVENTIVE_TASK_ASSIGNEE_ENDPOINT}/${preventiveTaskAssigneeId}`;

      const getResponse = await fetch(getUrl, {
        ...DEFAULT_GET_CONFIG,
      });

      await isErrorResponse(getResponse, null, dispatch);

      const preventiveTaskAssignee = await getResponse.json();

      dispatch({
        type: GET_PREVENTIVE_TASK_ASSIGNEE,
        payload: preventiveTaskAssignee,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagPreventiveTaskAssigneeActivity(false));
    }
  };

const clearPreventiveTaskAssignees = () => ({
  type: CLEAR_PREVENTIVE_TASK_ASSIGNEES,
});

const clearPreventiveTaskAssignee = () => ({
  type: CLEAR_PREVENTIVE_TASK_ASSIGNEE,
});

export {
  getPreventiveTaskAssignees,
  getPreventiveTaskAssigneesByPreventiveTaskId,
  getPreventiveTaskAssignee,
  postPreventiveTaskAssignee,
  putPreventiveTaskAssignee,
  patchDeactivatePreventiveTaskAssignee,
  clearPreventiveTaskAssignees,
  clearPreventiveTaskAssignee,
};
